import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { setAccessToken, clearAccessToken } from '../utils/apiClient';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authOpRef = useRef(0);
  const loginInProgressRef = useRef(false);

  const checkAuth = useCallback(async () => {
    if (loginInProgressRef.current) return;
    const opId = ++authOpRef.current;
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
      });
      // Ignore stale responses after login/logout or a newer checkAuth
      if (opId !== authOpRef.current || loginInProgressRef.current) return;
      const { access_token: _token, ...safeUser } = response.data || {};
      setUser(safeUser);
    } catch (error) {
      if (opId !== authOpRef.current || loginInProgressRef.current) return;
      if (error?.response?.status === 401) {
        clearAccessToken();
      }
      setUser(null);
    } finally {
      if (opId === authOpRef.current && !loginInProgressRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    const normalizedPassword = String(password || '');
    if (!normalizedEmail || !normalizedPassword) {
      const err = new Error('Email and password are required');
      err.response = { data: { detail: 'Email and password are required' } };
      throw err;
    }
    loginInProgressRef.current = true;
    // Invalidate any in-flight /auth/me so it cannot clear a fresh session
    authOpRef.current += 1;
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email: normalizedEmail, password: normalizedPassword },
        { withCredentials: true }
      );
      if (response.data?.access_token) {
        setAccessToken(response.data.access_token);
      }
      // Do not keep token on the user object in React state
      const { access_token: _token, ...safeUser } = response.data || {};
      setUser(safeUser);
      setLoading(false);
      return safeUser;
    } catch (error) {
      // Ensure failed login does not leave a half-set session
      throw error;
    } finally {
      // Keep the guard briefly so a racing checkAuth from other effects cannot wipe user
      setTimeout(() => {
        loginInProgressRef.current = false;
      }, 0);
    }
  };

  const logout = async () => {
    authOpRef.current += 1;
    loginInProgressRef.current = false;
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } finally {
      clearAccessToken();
      setUser(null);
      setLoading(false);
    }
  };

  const verifyOtp = async (email, otp, password) => {
    const response = await axios.post(
      `${API_URL}/api/auth/verify-otp`,
      { email, otp, password },
      { withCredentials: true }
    );
    return response.data;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    verifyOtp,
    checkAuth,
    isAuthenticated: !!user,
    isSuperadmin: user?.role === 'superadmin',
    isCeo: user?.role === 'ceo',
    isAdmin: user?.role === 'superadmin' || user?.role === 'ceo',
    isPeopleAdmin: user?.role === 'people_admin',
    isUserAdmin: user?.role === 'superadmin' || user?.role === 'ceo' || user?.role === 'people_admin',
    canManageUsers: user?.role === 'superadmin' || user?.role === 'ceo' || user?.role === 'people_admin',
    isManager: ['sales_manager', 'recruiting_manager', 'onsite_manager'].includes(user?.role),
    isLead: ['sales_lead', 'recruiting_lead'].includes(user?.role),
    isRecruiter: ['sales_recruiter', 'technical_recruiter', 'sales_technical_recruiter', 'sales_assistant_manager', 'recruiting_assistant_manager'].includes(user?.role),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
